/**
 * Optimizely Feature Experimentation client-side integration.
 * - Fires a decision event so Prismic (control) visitors register in the experiment Results
 * - Tracks conversion events to Feature Experimentation Results (and Optimizely Edge)
 *
 * Uses the same visitor ID (`optimizelyEndUserId` cookie) as the Cloudflare Worker
 * to keep bucketing consistent between server routing and client analytics.
 *
 */

import {
  createForwardingEventProcessor,
  createInstance,
  createPollingProjectConfigManager,
} from '@optimizely/optimizely-sdk'

const FLAG_KEY = 'homepage_prismic_vs_storyblok'

export const HOMEPAGE_VIEW_EVENT = '18649652273_gb_homepage'

type OptimizelyClient = ReturnType<typeof createInstance>

let clientReady: Promise<OptimizelyClient | null> | null = null

function getSdkKey(): string | null {
  const content = document
    .querySelector<HTMLMetaElement>('meta[name="optimizely-fx-sdk-key"]')
    ?.content.trim()

  return content !== undefined && content !== '' ? content : null
}

function getVisitorId(): string | null {
  const match = document.cookie.match(/(?:^|; )optimizelyEndUserId=([^;]*)/)

  return match !== null ? decodeURIComponent(match[1]) : null
}

function getClient(): Promise<OptimizelyClient | null> {
  const sdkKey = getSdkKey()

  if (sdkKey === null) return Promise.resolve(null)

  if (clientReady === null) {
    const instance = createInstance({
      projectConfigManager: createPollingProjectConfigManager({ sdkKey }),
      eventProcessor: createForwardingEventProcessor(),
    })

    clientReady = instance
      ? instance
          .onReady()
          .then((result) =>
            (result as { success?: boolean } | undefined)?.success !== false
              ? instance
              : null,
          )
          .catch(() => null)
      : Promise.resolve(null)
  }

  return clientReady
}

export function initFeatureExperimentation(): void {
  void getClient().then((client) => {
    const visitorId = getVisitorId()

    if (client === null || visitorId === null) return

    try {
      client.createUserContext(visitorId)?.decide(FLAG_KEY)
    } catch {
      /* Non-critical — silently skip if SDK not ready */
    }
  })
}

export function trackFeatureExperimentationEvent(name: string): void {
  void getClient().then((client) => {
    const visitorId = getVisitorId()

    if (client === null || visitorId === null) return

    try {
      client.createUserContext(visitorId)?.trackEvent(name)
    } catch {
      /* Non-critical — silently skip */
    }
  })
}

export function trackOptimizelyEvent(name: string): void {
  window.optimizelyEdge.push({ type: 'event', eventName: name })
  trackFeatureExperimentationEvent(name)
}
